import { useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../../providers/AuthProvider";
import indyLogo from "../../../assets/images/Indy-Logo.png";

type LoginFormData = {
  email: string;
  password: string;
};

type TwoFactorFormData = {
  code: string;
};

type TwoFactorSetup = {
  manualEntryKey: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
};

type AuthStage = "credentials" | "verify" | "setup";

export default () => {
  const { login, verifyTwoFactor, completeTwoFactorSetup } = useAuth();
  const [stage, setStage] = useState<AuthStage>("credentials");
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string>("");

  const loginForm = useForm<LoginFormData>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const verifyForm = useForm<TwoFactorFormData>({
    defaultValues: {
      code: "",
    },
  });

  const setupForm = useForm<TwoFactorFormData>({
    defaultValues: {
      code: "",
    },
  });

  const resetToCredentials = () => {
    setStage("credentials");
    setChallengeToken(null);
    setSetupToken(null);
    setSetup(null);
    setPendingEmail("");
    verifyForm.reset();
    setupForm.reset();
  };

  const handleLogin = async (data: LoginFormData) => {
    loginForm.clearErrors();

    try {
      const result = await login(data);

      if (result.status === "two-factor-required") {
        setStage("verify");
        setPendingEmail(result.user.email);
        setChallengeToken(result.challengeToken);
        verifyForm.reset();
        return;
      }

      if (result.status === "two-factor-setup-required") {
        setStage("setup");
        setPendingEmail(result.user.email);
        setSetupToken(result.setupToken);
        setSetup(result.setup);
        setupForm.reset();
      }
    } catch (err: any) {
      loginForm.setError("root", { message: err.message });
    }
  };

  const handleVerify = async (data: TwoFactorFormData) => {
    if (!challengeToken) {
      verifyForm.setError("root", { message: "Sign in again to continue" });
      return;
    }

    try {
      await verifyTwoFactor(challengeToken, data.code);
    } catch (err: any) {
      verifyForm.setError("root", { message: err.message });
    }
  };

  const handleSetup = async (data: TwoFactorFormData) => {
    if (!setupToken) {
      setupForm.setError("root", { message: "Sign in again to continue" });
      return;
    }

    try {
      await completeTwoFactorSetup(setupToken, data.code);
    } catch (err: any) {
      setupForm.setError("root", { message: err.message });
    }
  };

  return (
    <div className="min-h-screen bg-[#172939] flex items-center justify-center p-4">
      <div className={`w-full ${stage === "setup" ? "max-w-2xl" : "max-w-sm"} bg-[#f9563a] rounded-2xl p-8 shadow-xl`}>
        <div className="flex justify-center mb-6">
          <img src={indyLogo} alt="Indy" className="h-12 object-contain" />
        </div>

        {stage === "credentials" && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-extrabold text-[#172939]">Welcome Back</h1>
              <p className="text-[#172939]/70 mt-2 font-medium">Sign in to your account</p>
            </div>

            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5">
              {loginForm.formState.errors.root && (
                <div className="p-3 bg-red-900/30 border border-red-900/50 rounded-lg text-red-900 text-sm font-medium">
                  {loginForm.formState.errors.root.message}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-[#172939]/80 mb-1.5">Email</label>
                <input
                  type="email"
                  autoComplete="email"
                  {...loginForm.register("email", { required: "Email is required" })}
                  className="w-full px-4 py-3 bg-black/15 border border-[#172939]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#172939] focus:border-transparent transition-all"
                  placeholder="you@example.com"
                />
                {loginForm.formState.errors.email && (
                  <p className="mt-1.5 text-sm text-red-900 font-medium">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#172939]/80 mb-1.5">Password</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  {...loginForm.register("password", { required: "Password is required" })}
                  className="w-full px-4 py-3 bg-black/15 border border-[#172939]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#172939] focus:border-transparent transition-all"
                  placeholder="••••••••"
                />
                {loginForm.formState.errors.password && (
                  <p className="mt-1.5 text-sm text-red-900 font-medium">
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loginForm.formState.isSubmitting}
                className="w-full py-3 text-sm font-bold text-white bg-[#172939] hover:bg-[#172939]/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              >
                {loginForm.formState.isSubmitting ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </>
        )}

        {stage === "verify" && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-extrabold text-[#172939]">Enter Authenticator Code</h1>
              <p className="text-[#172939]/70 mt-2 font-medium">Finish signing in for {pendingEmail}</p>
            </div>

            <form onSubmit={verifyForm.handleSubmit(handleVerify)} className="space-y-5">
              {verifyForm.formState.errors.root && (
                <div className="p-3 bg-red-900/30 border border-red-900/50 rounded-lg text-red-900 text-sm font-medium">
                  {verifyForm.formState.errors.root.message}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-[#172939]/80 mb-1.5">6-digit code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  {...verifyForm.register("code", {
                    required: "Code is required",
                    pattern: {
                      value: /^\d{6}$/,
                      message: "Enter a valid 6-digit code",
                    },
                  })}
                  className="w-full px-4 py-3 bg-black/15 border border-[#172939]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#172939] focus:border-transparent transition-all"
                  placeholder="123456"
                />
                {verifyForm.formState.errors.code && (
                  <p className="mt-1.5 text-sm text-red-900 font-medium">{verifyForm.formState.errors.code.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={verifyForm.formState.isSubmitting}
                className="w-full py-3 text-sm font-bold text-white bg-[#172939] hover:bg-[#172939]/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifyForm.formState.isSubmitting ? "Verifying..." : "Verify Code"}
              </button>

              <button
                type="button"
                onClick={resetToCredentials}
                className="w-full py-3 text-sm font-semibold text-[#172939] bg-black/10 hover:bg-black/15 rounded-lg transition-colors"
              >
                Use a different account
              </button>
            </form>
          </>
        )}

        {stage === "setup" && setup && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-extrabold text-[#172939]">Set Up Google Authenticator</h1>
              <p className="text-[#172939]/70 mt-2 font-medium">
                Your tenant requires 2FA. Complete setup to continue as {pendingEmail}.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
              <div className="bg-white rounded-2xl p-4 flex items-center justify-center">
                <img src={setup.qrCodeDataUrl} alt="Two-factor QR code" className="w-full max-w-[180px]" />
              </div>

              <div>
                <div className="rounded-xl bg-black/10 p-4 mb-5">
                  <p className="text-sm font-semibold text-[#172939]">Manual entry key</p>
                  <p className="mt-2 font-mono text-sm break-all text-[#172939]">{setup.manualEntryKey}</p>
                </div>

                <form onSubmit={setupForm.handleSubmit(handleSetup)} className="space-y-5">
                  {setupForm.formState.errors.root && (
                    <div className="p-3 bg-red-900/30 border border-red-900/50 rounded-lg text-red-900 text-sm font-medium">
                      {setupForm.formState.errors.root.message}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-[#172939]/80 mb-1.5">Authenticator code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      autoComplete="one-time-code"
                      {...setupForm.register("code", {
                        required: "Code is required",
                        pattern: {
                          value: /^\d{6}$/,
                          message: "Enter a valid 6-digit code",
                        },
                      })}
                      className="w-full max-w-xs px-4 py-3 bg-black/15 border border-[#172939]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#172939] focus:border-transparent transition-all"
                      placeholder="123456"
                    />
                    {setupForm.formState.errors.code && (
                      <p className="mt-1.5 text-sm text-red-900 font-medium">
                        {setupForm.formState.errors.code.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={setupForm.formState.isSubmitting}
                    className="w-full md:w-auto px-6 py-3 text-sm font-bold text-white bg-[#172939] hover:bg-[#172939]/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {setupForm.formState.isSubmitting ? "Finishing..." : "Enable 2FA and Continue"}
                  </button>

                  <button
                    type="button"
                    onClick={resetToCredentials}
                    className="w-full md:w-auto md:ml-3 px-6 py-3 text-sm font-semibold text-[#172939] bg-black/10 hover:bg-black/15 rounded-lg transition-colors"
                  >
                    Use a different account
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
